<template>
  <component
    :is="componentTag"
    class="btn"
    :to="internalTo"
    :target="target"
    :external="external"
    :disabled="disabled"
    :class="classes"
    :type="type"
    @click="onClick">
    <span class="btn__label">
      <span
        v-if="(prefix || !!slots.prefix) && !noPrefix"
        class="btn__icon btn__prefix">
        <slot
          name="prefix"
          :icon="prefix">
          <a-icon :icon="prefix!" />
        </slot>
      </span>

      <slot />

      <span
        v-if="(suffix || !!slots.suffix) && !noSuffix"
        class="btn__icon btn__suffix">
        <slot
          name="suffix"
          :icon="suffix">
          <a-icon :icon="suffix!" />
        </slot>
      </span>
    </span>

    <span class="btn__loader">
      <transition-group
        appear
        name="list">
        <slot
          v-if="loading"
          name="loading">
          <a-spinner inherit-color />
        </slot>
        <slot
          v-if="success"
          name="success">
          <a-icon
            icon="octicon:check" />
        </slot>
        <slot
          v-if="failure"
          name="failure">
          <a-icon
            icon="octicon:x" />
        </slot>
      </transition-group>
    </span>
  </component>
</template>

<script setup lang="ts">
import type { DefineComponent } from "vue";
import type { RouteLocationRaw } from "vue-router";
import type { ButtonState } from "../../../types";
import { NuxtLink } from "#components";
import { computed, ref, watch, useSlots, useThemeConfig } from "#imports";

const emits = defineEmits( ["click", "loading", "success", "failure", "update:modelValue"] );

const props = withDefaults( defineProps<{
  /**
   * The model value for the button component.
   */
  modelValue?: ButtonState;
  /**
   * The route path for the button component.
   */
  to?: RouteLocationRaw;
  /**
   * Forces the link to be considered as external (true) or internal (false). This is helpful to handle edge-cases
   */
  external?: boolean;
  /**
   * The href for the button component.
   */
  href?: string;
  /**
   * The target attribute for the button component.
   */
  target?: string;
  /**
   * Whether the button component is disabled.
   */
  disabled?: boolean;
  /**
   * The type of the button component.
   */
  type?: string;
  /**
   * The action function to be executed on button click.
   */
  action?: ()=> object | Promise<()=> object>;
  /**
   * The minimum wait time in milliseconds before changing the button state.
   */
  minWaitTime?: number;
  /**
   * The variant of the button component.
   */
  variant?: string | null;
  /**
   * The look of the button component.
   */
  look?: string | null;
  /**
   * The size of the button component.
   */
  size?: "sm" | "md" | "lg";
  /**
   * An icon to prefix the button label with.
   */
  prefix?: string | null;
  /**
   * An icon to suffix the button label with.
   */
  suffix?: string | null;
  /**
   * Whether to turn off the prefix.
   */
  noPrefix?: boolean;
  /**
   * Whether to turn off the suffix.
   */
  noSuffix?: boolean;
}>(), {
  modelValue: null,
  to: undefined,
  external: false,
  href: undefined,
  target: undefined,
  type: "button",
  action: undefined,
  minWaitTime: 1000,
  variant: "primary",
  look: "default",
  size: "md",
  prefix: undefined,
  suffix: undefined,
  noPrefix: false,
  noSuffix: false,
} );

const slots = useSlots();

const themeConfig = await useThemeConfig();

// Define the reactive variables
const state = ref<ButtonState | null>( null );
const loading = computed( () => state.value && state.value === "loading" );
const success = computed( () => state.value && state.value === "success" );
const failure = computed( () => state.value && state.value === "failure" );

// Watch for changes in the modelValue prop and update the state variable
watch( () => props.modelValue, ( val ) => {
  state.value = val;
}, { immediate: true } );

// Watch for changes in the state variable and emit the updated value
watch( state, ( val ) => {
  emits( "update:modelValue", val );
} );

// Compute the component tag based on to and href props
const componentTag = computed( () => {
  let result: string | DefineComponent = "button";

  if ( props.to || props.href ) {
    result = NuxtLink;
  }

  return result;
} );

const disabled = computed( () => {
  return props.disabled || loading.value || success.value || failure.value;
} );

const internalTo = computed( () => {
  if ( props.disabled ) {
    return "javascript:void(0)";
  }

  return props.to || props.href || "";
} );

const classes = computed( () => {
  const result = [];

  if ( props.variant ) {
    result.push( `btn-${props.variant}` );
  }

  if ( look.value ) {
    result.push( `btn-${look.value}` );
  }

  if ( props.size ) {
    result.push( `btn--${props.size}` );
  }

  if ( loading.value || success.value || failure.value ) {
    result.push( "btn--loader-visible" );
  }

  if ( disabled.value ) {
    result.push( "btn--disabled" );
  }

  return result;
} );

const look = computed( () => props.look || "default" );

const prefix = computed( () => {
  if ( props.prefix !== undefined ) {
    return props.prefix;
  }

  return themeConfig.value?.buttons?.looks?.[look.value]?.prefix;
} );

const suffix = computed( () => {
  if ( props.suffix !== undefined ) {
    return props.suffix;
  }

  return themeConfig.value?.buttons?.looks?.[look.value]?.suffix;
} );

async function onClick () {
  if ( loading.value ) {
    return;
  }

  emits( "click" );

  if ( props.action ) {
    await processAction();
  }
}

async function processAction () {
  const minWaitPromise = new Promise( resolve => setTimeout( resolve, 2000 ) );

  state.value = "loading";
  emits( "loading", true );
  try {
    await Promise.all( [minWaitPromise, props.action?.()] );
    state.value = "success";
    emits( "success" );
  } catch ( err ) {
    state.value = "failure";
    emits( "failure", err );
    throw err;
  } finally {
    emits( "loading", false );
    clearStates( 2000 );
  }
}

// Clear the states after the given timeout
function clearStates ( timeoutMs: number ) {
  setTimeout( () => {
    state.value = null;
  }, timeoutMs );
}
</script>

<style lang="scss" scoped>
.btn {
  display: inline-flex;
  position: relative;
  min-width: 50px;
  gap: .5rem;
  justify-content: center;
  align-items: center;
  font-weight: 400;
  white-space: nowrap;
  padding: .375rem .75rem;
  font-size: 1rem;
  line-height: 1.5;
  border: 1px solid transparent;
  flex-shrink: 0;
  transition: background-color var(--transition-time) ease-out,
  color var(--transition-time) ease-out,
  box-shadow var(--transition-time) ease-out,
  border-color var(--transition-time) ease-out;

  &:hover {
    cursor: pointer;
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &--disabled,
  &.disabled,
  &[disabled],
  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }

  &--loader-visible &__label {
    visibility: hidden;
  }

  &--loader-visible &__loader {
    opacity: 1;

    &:hover {
      cursor: initial;
    }
  }

  &__loader {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    opacity: 0;
    transition: opacity .1s ease-out;
  }

  &__label {
    display: flex;
    align-items: center;
    gap: var(--s-2);
  }

  &--sm {
    font-size: 0.8rem;
  }

  &--lg {
    font-size: 1.2rem;
  }
}

.list-enter-active,
.list-leave-active {
  transition: opacity 0.5s ease;
  position: absolute;
}
.list-enter-from,
.list-leave-to {
  opacity: 0;
}
</style>
